/**
 * Basisklasse für alle Restraints.
 * Restraints wirken auf das gedraggte Objekt und können deren Position, Orientierung und Skalierung beeinflussen.
 * Daneben können Restraints auch als Drag/Drop-Sensoren wirken (onEnter/onLeave). Wenn man einen
 * Restraint nur als Sensor benutzen möchte, überschreibt man perform leer.
 */
import Commons from "oc-commons/Commons";
import Scenes from "oc-threejs/Scenes";

export default class Restraint {

    /** @type {number} */
    order;
    /** @type {number} */
    priority;
    /** @type {boolean} */
    alternating;
    /** @type {boolean} */
    additively;
    /** @type {?THREE.Object3D} */
    parentObject3D;
    /** @type {THREE.Matrix4} */
    matrixWorld;
    /** @type {THREE.Matrix4} */
    matrixWorldInverse;
    /** @type {?THREE.Object3D} */
    dragObject3d;
    /** @type {?THREE.Box3} */
    dragObject3dBoundingBoxLocal;
    /** @type {?THREE.Object3D} */
    visualization;
    /** @type {string} */
    name;
    /** @type {boolean} */
    freeOrientation;

    /**
     * Ein Restraint hat eine Priority. Der Restraint mit der höchsten Priority, für den accept true ist, wird ausgeführt.
     * Mit der Option additively==true wird der Restraint zusätzlich zu anderen Restraints aktiv, falls seine raycast-Methode einen Punkt zurückgibt
     * @param options
     */
    constructor(options) {
        this.priority =  (options && options.priority) || 0;
        this.alternating = (options && options.alternating) || false;
        this.additively =  (options && options.additively) || false;
        this.parentObject3D = (options && options.parentObject3D) || null;
        this.matrixWorld = (options && options.matrixWorld)
                            || (this.parentObject3D && this.parentObject3D.matrixWorld.clone())
                            || new THREE.Matrix4();
        this.matrixWorldInverse = new THREE.Matrix4().copy(this.matrixWorld).invert();
        this.name = (options && options.name) || Commons.typeName(this);
        this.freeOrientation = (options && options.freeOrientation) || false;
    }

    /**
     * @param {THREE.Object3D} dragObject3d
     * @param {THREE.Box3} dragObject3dBoundingBoxLocal
     */
    setDragObject3d(dragObject3d, dragObject3dBoundingBoxLocal) {
        this.dragObject3d = dragObject3d;
        this.dragObject3dBoundingBoxLocal = dragObject3dBoundingBoxLocal;
    }

    /**
     * prüft, ob dieser Restraint gültig ist
     * @returns {boolean}
     */
    accept() {
        return true;
    }

    /**
     * prüft, ob der Ray aus einer gültigen Richtung kommt. Damit können Mausklicks auf bestimmte
     * Blickrichtungen eingeschränkt werden, z.B. nur von vorne innerhalb von 60 Grad:
     *         return ray.direction.angleTo(Maths3.vector.zneg)*180/Math.PI < 60;
     * @returns {boolean}
     */
    acceptRay(ray) {
        return true;
    }

    /**
     * Gibt die potentielle Drag/Drop-Position des Objektes im lokalen Koordinatensystem des Restraints zurück.
     * @param {THREE.Ray} ray welcher durch die Maus als Sehstrahl in die Szene geht im lokalen Koordinatensystem des Restraints
     * @returns {?THREE.Vector3} gibt im Falle einer Intersection die neue Position des Objektes zurück

     */
    raycastLocal(ray) {
    }

    /**
     * Gibt die potentielle Drag/Drop-Position des Objektes zurück
     * @param {THREE.Raycaster} raycaster welcher durch die Maus als Sehstrahl in die Szene geht
     * @returns {?{distance:number, point:THREE.Vector3, restraint:Restraint}} gibt im Falle einer Intersection ein Objekt mit distance, point und restraint (this) zurück
     */
    raycastWorld(raycaster) {
        const ray = raycaster.ray.clone();
        ray.applyMatrix4(this.matrixWorldInverse);
        if (!this.acceptRay(ray)) {
            return null;
        }
        const point = this.raycastLocal(ray);
        if (!point) {
            return null;
        }
        point.applyMatrix4(this.matrixWorld);
        return {
            distance: raycaster.ray.origin.distanceTo(point),
            point,
            restraint: this,
        };
    }

    /**
     * Wird ausgeführt, wenn dieser Restraint gewonnen hat
     * (accept ist true und der von raycast zurückgegebene Punkt ist nicht null,
     * und der Restraint hat die höchste Prio oder er hat additively==true).
     * Hier ist man komplett frei, was der Restraint letztlich tut, aber normalerweise wird dragObject3d.position auf point gesetzt.
     * @param {THREE.Raycaster} raycaster welcher durch die Maus als Sehstrahl in die Szene geht
     * @param {THREE.Vector3} point die von raycast zurückgegebene potientielle neue Position
     * @param {boolean} dropped Boolean, false für drag und true für drop
     */
    perform(raycaster, point, dropped) {
        if (!this.freeOrientation) {
            this.dragObject3d.copyMatrix(this.matrixWorld);
        }
        this.dragObject3d.position.copy(this.dragObject3d.parent ? this.dragObject3d.parent.worldToLocal(point.clone()) : point);
    }

    /**
     * wird beim drop des gedraggte Objektes aufgerufen, wenn der Restraint aktiv ist
     */
    onDrop() {
    }

    /**
     * wird aufgerufen, sobald der Einflussbereich des Restraints betreten wird und der Restraint aktiv wird.
     * Achtung! Wird erst nach perform aufgerufen! Wird auch aufgerufen, wenn das Objekt vor dem DragStart schon im Einflussbereich war.
     */
    onEnter() {
    }

    /**
     * wird aufgerufen, sobald der Einflussbereich des Restraints verlassen wird und der Restraint inaktiv wird,
     * @param real true wenn das gedraggte Objekt wirklich den Einflussbereich verlassen hat, false wenn es im Einflussbereich gedropped wurde
     *        oder es sich um eine Aufräumaktion als Teil des dragEnd handelt
     */
    onLeave(real) {
    }

    /**
     * onDrag3dSceneEnter wird als Teil des normalen Lifecycles bei jedem
     * Drag-Vorgang geworfen.
     */
    onDrag3dSceneEnter() {
    }

    /**
     * wird beim Verlassen der Szene aufgerufen, siehe onDrag3dSceneEnter
     * @param real true if the mouse goes to the outside
     */
    onDrag3dSceneLeave(real) {
    }

    /**
     * wird beim Start des Dragvorganges aufgerufen
     */
    onDrag3dBegin() {
    }

    /**
     * wird beim Ende des Dragvorganges aufgerufen
     */
    onDrag3dEnd() {
    }


    /**
     * wird bei jedem Dragvorgang aufgerufen, wenn der Restraint gültig ist, und zwar garantert nach onEnter, im Gegensatz
     * zu perform. Die Position ist dann auch schon die endgültige.
     */
    onDrag3d() {
    }

    /**
     * erzeugt eine Object3D-Representation, um den Restraint anzuzeigen, und gibt das Objekt3D zurück
     * @returns {?THREE.Object3D}
     */
    createVisualization(restraintHandler) {
        return null;
    }

    /**
     * erzeugt eine Debug-Object3D-Representation, um den Restraint anzuzeigen, und gibt das Debug-Objekt3D zurück
     * @returns {?THREE.Object3D}
     */
    createDebugVisualization(restraintHandler) {
        return null;
    }

}
